import { useCallback, useEffect } from 'react';
import { View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { useDialog } from '@/hooks/useDialog';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { SetType } from '@/components/Template/types';
import { SetEditorFlow } from '@/contexts/SetEditorContext';
import { useSetEditor } from '@/contexts/SetEditorContext/useSetEditor';
import { useSetEditorCancel } from '@/hooks/useSetEditorCancel';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import Card from '@/components/ui/therapist/Card';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Pill } from '@/components/Pill';
import { IconName } from '@/components/NeolexonIcon/Icons/types';
import { Text } from '@/components/ui/therapist/Text';
import { Icon } from '@/components/Icon';
import RadioButton from '@/components/ui/therapist/RadioButton';
import { Button } from '@/components/ui/therapist/Button';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { TemplateChoiceBanner } from '@/components/TemplateChoiceBanner';
import { styles } from './styles';
import colors from '@/config/colors';
import theme from '@/config/theme';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';

const setsNavigation = {
  wordsets: { title: 'Wörterset', navigation: 'WordSet', icon: 'letter' },
  sentencesets: {
    title: 'Sätzeset',
    navigation: 'SentenceSet',
    icon: 'sentence',
  },
  textsets: { title: 'Texteset', navigation: 'TextSet', icon: 'reading' },
} as const;

const templateSetType = {
  wordsets: 'Word',
  sentencesets: 'Sentence',
} as const;

type SetStackParamList = {
  WordSet: { selectedID: string; flow: SetEditorFlow };
  SentenceSet: { selectedID: string; flow: SetEditorFlow };
  TextSet: { selectedID: string; flow: SetEditorFlow };
};

type SetCreationProps = StackScreenProps<
  TherapistStackParamList,
  'SetCreation'
>;

export function SetCreation({ navigation, route }: SetCreationProps) {
  const { formData, setFormData } = useSetEditor();
  const { showDialog } = useDialog();
  const patientId = route.params.selectedID ?? '';
  const flow = route.params.flow;

  const isTemplateMode = flow === 'createTemplate';

  const cancel = useSetEditorCancel(flow, patientId);

  const templateBannerType =
    !isTemplateMode && formData.type
      ? templateSetType[formData.type as keyof typeof templateSetType]
      : undefined;

  useEffect(() => {
    navigation.setOptions({
      title: isTemplateMode ? 'Neue Vorlage erstellen' : 'Set erstellen',
      headerTitleAllowFontScaling: false,
      headerRight: () => (
        <View style={{ gap: 30, flexDirection: 'row' }}>
          <AppHeaderAction
            primary
            onPress={cancel}
            icon={<Icon name="close" scale={1.2} />}
            text="Abbrechen"
          />
        </View>
      ),
    });
  }, [navigation, isTemplateMode, cancel]);

  const handleTypeChange = useCallback(
    (type: SetType) => {
      setFormData({ type });
    },
    [setFormData],
  );

  const handleNext = useCallback(async () => {
    if (!formData.type) {
      return;
    }

    const hasInternet = await hasInternetAsync();
    if (!hasInternet) {
      showDialog(PREPARED_DIALOGS.noInternetDialog());
      return;
    }

    const setNavigation = setsNavigation[formData.type];

    navigation.navigate(setNavigation.navigation as keyof SetStackParamList, {
      selectedID: patientId,
      flow,
      apiName: formData.type,
    });
  }, [formData.type, flow, navigation, patientId, showDialog]);

  return (
    <BodyWrapper showSidebar={false} bodyStyle={{ width: '70%', flex: 1 }}>
      <SafeAreaView style={styles.container}>
        <Card>
          <Text style={styles.sectionTitle}>
            {isTemplateMode ? 'Neue Vorlage erstellen' : 'Neues Set erstellen'}
          </Text>
          <View style={styles.section}>
            <Text style={[styles.fieldLabel, { marginTop: theme.spacings.lg }]}>
              Set-Typ
              <Text style={{ color: colors.newUI.therapist.red70 }}> *</Text>
            </Text>
            <Text style={styles.sectionDescription}>
              Legen Sie fest, welche Inhalte in Ihrem Set enthalten sein sollen.
            </Text>
            <View style={styles.typeSelection}>
              {Object.entries(setsNavigation)
                .filter(([key]) => !(isTemplateMode && key === 'textsets'))
                .map(([key, set]) => {
                  const typedKey = key as SetType;
                  const isSelected = formData.type === typedKey;

                  return (
                    <PressableNeolexon
                      key={typedKey}
                      onPress={() => handleTypeChange(typedKey)}
                    >
                      <View style={styles.typeItem}>
                        <RadioButton
                          isSelected={isSelected}
                          onPress={() => handleTypeChange(typedKey)}
                        />
                        <Pill
                          label={set.title}
                          icon={set.icon as IconName}
                          isNeolexon={false}
                          iconSize={22}
                        />
                      </View>
                    </PressableNeolexon>
                  );
                })}
            </View>
          </View>
        </Card>

        {templateBannerType ? (
          <View style={styles.banner}>
            <TemplateChoiceBanner
              type={templateBannerType}
              selectedID={patientId}
            />
          </View>
        ) : null}

        <View style={styles.footer}>
          <Button label="Abbrechen" look="secondary" onPress={cancel} />
          <Button
            label="Weiter"
            disabled={!formData.type}
            onPress={handleNext}
          />
        </View>
      </SafeAreaView>
    </BodyWrapper>
  );
}
